import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { Pagination, PaginationItem } from 'types/capi/plp';

import ArrowIcon from '~/assets/icons/Arrow';
import Tracked from '~/components/Tracked';

type Props = {
  pagination: Pagination;
};

export default function Pagination({ pagination }: Props) {
  const { t } = useTranslation('plp');

  if (!pagination.pages) return null;

  return (
    <div className="flex h-11 justify-between">
      <NavigationButton item={pagination.previous_page}>
        <span>
          <ArrowIcon />
        </span>
        <span className="hidden lg:block">{t('previous')}</span>
      </NavigationButton>
      <PageButtons pagination={pagination} />
      <NavigationButton item={pagination.next_page}>
        <span className="hidden lg:block">{t('next')}</span>
        <span className="rotate-180">
          <ArrowIcon />
        </span>
      </NavigationButton>
    </div>
  );
}

function NavigationButton({
  item,
  children
}: {
  children: React.ReactNode;
  item?: PaginationItem;
}) {
  return (
    <Link to={item?.path ?? ''} data-cy="pagination-button">
      <TrackedButton
        className="flex w-12 items-center justify-center gap-2 font-light lg:w-32"
        disabled={!item}
        name={`page_${item?.text.toUpperCase()}`}
      >
        {children}
      </TrackedButton>
    </Link>
  );
}

function PageButtons({ pagination }: { pagination: Pagination }) {
  const { pages, next_page, previous_page } = pagination;

  if (!pages) return null;

  return (
    <div className="flex h-11 gap-4">
      {pages.map((page, idx) => (
        <React.Fragment key={`page-button-${idx}`}>
          {idx === 1 && pages.length > 3 && previous_page && <Ellipsis />}
          <PageButton item={page} className={getPageButtonStyle(idx, pages)} />
          {idx === 3 && next_page && <Ellipsis />}
        </React.Fragment>
      ))}
    </div>
  );
}

function PageButton({
  item,
  className
}: {
  className?: string;
  item: PaginationItem;
}) {
  return (
    <Link to={item.path} data-cy="page-button" className={className}>
      <TrackedButton
        active={item.active}
        className={`w-12 px-0 ${className}`}
        name={`page_${item.text?.toUpperCase()}`}
      >
        {item.text}
      </TrackedButton>
    </Link>
  );
}

function getPageButtonStyle(position: number, allItems: PaginationItem[]) {
  const activePosition = allItems.findIndex((i) => i.active);

  if (activePosition > position && position === 1) return 'hidden md:block';
  if (activePosition > position && position === 2) return 'hidden sm:block';

  if (activePosition < position && position === 2) return 'hidden sm:block';
  if (activePosition < position && position === 3) return 'hidden md:block';
}

function TrackedButton({
  active,
  children,
  className,
  disabled,
  name
}: {
  active?: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  name: string;
}) {
  const disabledStyles =
    'opacity-50 cursor-not-allowed active:scale-100 hover:opacity-50';

  return (
    <Tracked.Button
      className={`${className} ${disabled ? disabledStyles : ''} h-full`}
      disabled={disabled}
      variant={active ? 'black' : 'transparent-black-border'}
      name={`PAGINATION_${name.toUpperCase()}`}
      pagePosition="Body"
    >
      {children}
    </Tracked.Button>
  );
}

function Ellipsis() {
  return (
    <div className="flex w-2 items-center justify-center text-lg font-semibold text-grey-hide md:w-4">
      <div>...</div>
    </div>
  );
}
